import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Portal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { assign, filter, find, flatten, isNull, map, spread } from 'lodash'

import { Dictionaries, TechnicalCondition } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  CancelIcon,
  CheckedIcon,
  CheckIcon,
  DeleteIcon,
  SaveIcon,
  WarnIcon,
} from '../icons'

const mergeByProperty = (target, source, prop) =>
  source.map((sourceElement) => {
    const targetElement = find(target, (t) => sourceElement[prop] === t[prop])
    return targetElement
      ? assign(targetElement, sourceElement)
      : {
          ...sourceElement,
          technicalImages: [],
        }
  })

function ControlsEditUsedCarControl() {
  const { controlId, portalRef, onCancelClick } = useOutletContext()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const [technicalConditionsDictionary, setTechnicalConditionsDictionary] =
    useState([])
  const [values, setValues] = useState([])
  const [isBtnLoading, setIsBtnLoading] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    Promise.all([
      Dictionaries.getTechnicalConditionsDictionary(axiosPrivate),
      TechnicalCondition.getTechnicalCondition(axiosPrivate, controlId),
    ])
      .then(
        spread((_dictionary, _values) => {
          const newDictionary = map(_dictionary.data, (group) => ({
            ...group,
            technicalChecklistGroupId: group.id,
            expanded: false,
          }))

          setTechnicalConditionsDictionary(newDictionary)

          setValues({
            description: isNull(_values.data.description)
              ? ''
              : _values.data.description,
            getTechnicalConditionGroupModel: mergeByProperty(
              _values.data.getTechnicalConditionGroupModel,
              newDictionary,
              'technicalChecklistGroupId',
            ),
          })
        }),
      )
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, controlId])

  const saveTechnicalConditions = (redirect) => (e) => {
    e.preventDefault()
    setIsBtnLoading(true)

    const payload = flatten(
      map(values.getTechnicalConditionGroupModel, (group) =>
        map(
          group.technicalChecklistGroupElements,
          ({
            technicalChecklistItemId,
            technicalConditionStatus,
            description,
          }) => ({
            technicalChecklistItemId,
            technicalConditionStatus,
            description,
          }),
        ),
      ),
    )

    TechnicalCondition.saveTechnicalCondition(axiosPrivate, controlId, {
      technicalConditions: payload,
      description: values.description,
    })
      .then(() => {
        if (redirect) {
          navigate(redirect)
        }
      })
      // eslint-disable-next-line no-console
      .catch((er) => console.error(er))
      .finally(() => {
        setIsBtnLoading(false)
      })
  }

  const saveTechnicalConditionImage = (technicalChecklistGroupId) => (e) => {
    const fileForm = new FormData()
    fileForm.append('file', e.target.files[0])

    TechnicalCondition.saveTechnicalConditionImage(
      axiosPrivate,
      controlId,
      technicalChecklistGroupId,
      fileForm,
    )
      .then(({ data: { data } }) => {
        setValues({
          ...values,
          getTechnicalConditionGroupModel: map(
            values.getTechnicalConditionGroupModel,
            (group) => {
              if (
                group.technicalChecklistGroupId === technicalChecklistGroupId
              ) {
                return {
                  ...group,
                  technicalImages: [...group.technicalImages, data],
                }
              }
              return group
            },
          ),
        })
      })
      // eslint-disable-next-line no-shadow, no-console
      .catch((e) => console.error(e))
  }

  const deleteTechnicalConditionImage =
    (technicalChecklistGroupId, imageId) => () => {
      TechnicalCondition.deleteTechnicalConditionImage(
        axiosPrivate,
        controlId,
        technicalChecklistGroupId,
        imageId,
      )
        .then(() => {
          setValues({
            ...values,
            getTechnicalConditionGroupModel: map(
              values.getTechnicalConditionGroupModel,
              (group) => {
                if (
                  group.technicalChecklistGroupId === technicalChecklistGroupId
                ) {
                  return {
                    ...group,
                    technicalImages: filter(
                      group.technicalImages,
                      (o) => o.id !== imageId,
                    ),
                  }
                }
                return group
              },
            ),
          })
        })
        // eslint-disable-next-line no-shadow, no-console
        .catch((e) => console.error(e))
    }

  const handleChangeRadios = (data) => (e) => {
    e.preventDefault()
    let newGroupElements

    const elementsGroup = find(values.getTechnicalConditionGroupModel, {
      technicalChecklistGroupId: data.groupId,
    })
    if (elementsGroup) {
      const element = find(elementsGroup.technicalChecklistGroupElements, {
        technicalChecklistItemId: data.elementId,
      })
      if (element) {
        newGroupElements = map(
          values.getTechnicalConditionGroupModel,
          (item) => {
            if (item.technicalChecklistGroupId === data.groupId) {
              const newElements = map(
                item.technicalChecklistGroupElements,
                (elem) => {
                  if (elem.technicalChecklistItemId === data.elementId) {
                    return { ...elem, technicalConditionStatus: e.target.value }
                  }
                  return elem
                },
              )
              return {
                ...item,
                technicalChecklistGroupElements: newElements,
              }
            }
            return item
          },
        )
      } else {
        newGroupElements = map(
          values.getTechnicalConditionGroupModel,
          (item) => {
            if (item.technicalChecklistGroupId === data.groupId) {
              return {
                ...item,
                technicalChecklistGroupElements: [
                  ...(item.technicalChecklistGroupElements || []),
                  {
                    description: '',
                    technicalChecklistGroupId: data.groupId,
                    technicalChecklistItemId: data.elementId,
                    technicalConditionStatus: e.target.value,
                  },
                ],
              }
            }
            return item
          },
        )
      }
    } else {
      newGroupElements = [
        ...values.getTechnicalConditionGroupModel,
        {
          technicalChecklistGroupId: data.groupId,
          technicalChecklistGroupElements: [
            {
              description: '',
              technicalChecklistGroupId: data.groupId,
              technicalChecklistItemId: data.elementId,
              technicalConditionStatus: e.target.value,
            },
          ],
        },
      ]
    }

    setValues({
      description: values.description,
      getTechnicalConditionGroupModel: newGroupElements,
    })
  }

  const handleChangeDesc = (currentItem) => (e) => {
    e.preventDefault()

    const newGroupElements = map(
      values.getTechnicalConditionGroupModel,
      (item) => {
        if (
          item.technicalChecklistGroupId ===
          currentItem.technicalChecklistGroupId
        ) {
          const newElements = map(
            item.technicalChecklistGroupElements,
            (elem) => {
              if (
                elem.technicalChecklistItemId ===
                currentItem.technicalChecklistItemId
              ) {
                return { ...elem, description: e.target.value }
              }
              return elem
            },
          )
          return {
            ...item,
            technicalChecklistGroupElements: newElements,
          }
        }
        return item
      },
    )
    setValues({
      description: values.description,
      getTechnicalConditionGroupModel: newGroupElements,
    })
  }

  const handleExpandAccordion = (groupId, expanded) => () => {
    setTechnicalConditionsDictionary(
      map(technicalConditionsDictionary, (group) => {
        if (group.id === groupId) {
          return { ...group, expanded }
        }
        return group
      }),
    )
  }

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<CheckIcon />}
          title={t('usedControlTitle')}
          subtitle={t('usedControlSubTitle')}
        />

        <Box>
          {map(
            technicalConditionsDictionary,
            ({ id: groupId, name, imageRequired, expanded, items }, key) => {
              const currentGroup =
                find(values.getTechnicalConditionGroupModel, {
                  technicalChecklistGroupId: groupId,
                }) || {}
              return (
                <Accordion
                  key={key}
                  expanded={expanded}
                  onChange={handleExpandAccordion(groupId, !expanded)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${groupId}a-content`}
                    id={`panel${groupId}a-header`}
                  >
                    <Typography variant="h6">{name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid item sm={12} key={key}>
                      <List>
                        {map(items, (item) => {
                          const currentItem =
                            find(currentGroup.technicalChecklistGroupElements, [
                              'technicalChecklistItemId',
                              item.id,
                            ]) || {}

                          return (
                            <ListItem
                              key={item.id}
                              sx={{
                                alignItems: 'flex-start',
                                px: {
                                  xs: 0,
                                  md: 2,
                                },
                              }}
                            >
                              <Grid container>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    sx={{
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-start',
                                    }}
                                  >
                                    <ListItemIcon>
                                      {currentItem?.technicalConditionStatus ===
                                      'Ok' ? (
                                        <CheckedIcon />
                                      ) : (
                                        <WarnIcon />
                                      )}
                                    </ListItemIcon>
                                    <Typography variant="body2">
                                      {item.name}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      flexGrow: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'row',
                                        justifyContent: {
                                          xs: 'flex-start',
                                          md: 'flex-end',
                                        },
                                        mt: {
                                          xs: 2,
                                          md: 0,
                                        },
                                        ml: {
                                          xs: 6,
                                          md: 0,
                                        },
                                      }}
                                    >
                                      <FormControl>
                                        <RadioGroup
                                          name="technicalConditionStatus"
                                          value={
                                            currentItem?.technicalConditionStatus ||
                                            ''
                                          }
                                          onChange={handleChangeRadios({
                                            groupId,
                                            elementId: item.id,
                                          })}
                                          row
                                        >
                                          <FormControlLabel
                                            value="Ok"
                                            control={<Radio />}
                                            label="Dobrze"
                                            labelPlacement="top"
                                            componentsProps={{
                                              typography: {
                                                variant: 'caption',
                                              },
                                            }}
                                            sx={{
                                              mx: {
                                                xs: 1,
                                                lg: 2,
                                              },
                                            }}
                                          />
                                          <FormControlLabel
                                            value="NotOk"
                                            control={<Radio />}
                                            label="Źle"
                                            labelPlacement="top"
                                            componentsProps={{
                                              typography: {
                                                variant: 'caption',
                                              },
                                            }}
                                            sx={{
                                              mx: {
                                                xs: 1,
                                                lg: 2,
                                              },
                                            }}
                                          />
                                          <FormControlLabel
                                            value="NotApplicable"
                                            control={<Radio />}
                                            label="Nie dotyczy"
                                            labelPlacement="top"
                                            componentsProps={{
                                              typography: {
                                                variant: 'caption',
                                              },
                                            }}
                                            sx={{
                                              mx: {
                                                xs: 1,
                                                lg: 2,
                                              },
                                            }}
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </Box>
                                    {currentItem?.technicalConditionStatus ===
                                      'NotOk' && (
                                      <TextField
                                        name="description"
                                        placeholder="Praca do wykonania"
                                        value={currentItem.description}
                                        onChange={handleChangeDesc(currentItem)}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                      />
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </ListItem>
                          )
                        })}
                      </List>
                      {imageRequired && (
                        <Box>
                          <Divider />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              py: 1,
                            }}
                          >
                            <Button
                              component="label"
                              size="small"
                              startIcon={<PhotoCamera />}
                              sx={{ p: 1 }}
                            >
                              {t('carEditDamageAddPhoto')}
                              <input
                                name={`image[${groupId}]`}
                                hidden
                                accept="image/*"
                                multiple
                                type="file"
                                onChange={saveTechnicalConditionImage(
                                  currentGroup.technicalChecklistGroupId,
                                )}
                                style={{ display: 'none' }}
                              />
                            </Button>
                          </Box>
                          <Box>
                            {currentGroup.technicalImages?.length ? (
                              <Grid container spacing={1}>
                                {map(
                                  currentGroup.technicalImages,
                                  ({ imageUrl, id: imageId }, imgKey) => (
                                    <Grid
                                      key={imgKey}
                                      item
                                      xs={4}
                                      sx={{ textAlign: 'center' }}
                                    >
                                      <Box
                                        sx={({ palette }) => ({
                                          position: 'relative',
                                          width: '100%',
                                          height: 120,
                                          backgroundColor: palette.gray.dark,
                                          backgroundImage: `url(${imageUrl})`,
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center',
                                        })}
                                      >
                                        <IconButton
                                          disableRipple
                                          size="small"
                                          sx={({ palette }) => ({
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            p: '4px',
                                            backgroundColor:
                                              palette.primary.main,
                                            fill: palette.primary.contrastText,
                                          })}
                                          onClick={deleteTechnicalConditionImage(
                                            currentGroup.technicalChecklistGroupId,
                                            imageId,
                                          )}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  ),
                                )}
                              </Grid>
                            ) : (
                              <Typography
                                align="center"
                                component="div"
                                variant="caption"
                                sx={{ p: 1 }}
                              >
                                {t('carEditDamageNoPhoto')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ textAlign: 'center' }}>
                        <Button
                          size="small"
                          disableRipple
                          startIcon={<ExpandLessIcon />}
                          onClick={handleExpandAccordion(groupId, false)}
                          sx={{
                            mt: 2,
                            py: 1,
                          }}
                        >
                          {t('carEditHide')}
                        </Button>
                      </Box>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )
            },
          )}
        </Box>
        <Portal container={portalRef.current}>
          <ButtonBox
            buttons={[
              {
                label: 'Wstecz',
                icon: <ArrowLeft fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/edit/tyres`,
                  ),
                },
              },
              {
                label: t('backToListBtnLabel'),
                icon: <CancelIcon />,
                props: {
                  variant: 'outlined',
                  color: 'primary',
                  onClick: onCancelClick,
                },
              },
              {
                label: 'Zapisz',
                icon: <SaveIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(false),
                },
              },
              {
                label: 'Zakończ',
                icon: <CheckIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/summary`,
                  ),
                },
              },
              {
                label: t('nextBtnLabel'),
                icon: <ArrowRight fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/edit/todo`,
                  ),
                },
              },
            ]}
          />
        </Portal>
      </Section>
    </div>
  )
}

export default ControlsEditUsedCarControl
